import { CapacityOptionItem, SortOptionItem } from '@/app/[lang]/offers/types'
import { CardType, Country, PaginatedList } from '@/types'
import { PlanDetails } from '@/types/plan'

export function getCardType(cardType: CardType | any) {
  switch (cardType) {
    case CardType.DataOnly:
      return 'OfferCard.Text.DataCard'

    case CardType.VoiceAndData:
      return 'OfferCard.Text.VoiceCard'

    default:
      return ''
  }
}

export function encodeDestination(destination: string) {
  return encodeURIComponent(destination.toLowerCase().replace(/ /g, '-'))
}

export function decodeDestination(destination: string) {
  return decodeURIComponent(destination.toLowerCase().replace(/-/g, ' '))
}

export function destinationToSlug(destination: string) {
  return encodeURIComponent(destination.toLowerCase().replace(/ /g, '-'))
}

export function slugToDestination(slug: string) {
  return decodeURIComponent(slug.replace(/-/g, ' '))
}

export function filterDestination(c: Country, inputValue: string) {
  const _input = inputValue.toLowerCase()
  const en = c?.en || ''
  const bs = c?.bs || ''
  const hr = c?.hr || ''
  const sr = c?.sr || ''
  const name = c?.name
  return filterCountries(en, _input) || filterCountries(bs, _input) || filterCountries(hr, _input) || filterCountries(sr, _input) || filterCountries(name, _input)
}

export function normalizeString(str: string, replace: string = '') {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, replace)
}

export function filterCountries(country: string, inputValue: string) {
  if (!inputValue) {
    return true
  }

  const _input = normalizeString(inputValue.toLowerCase())

  return normalizeString(country.toLowerCase()).includes(_input)
}

export function equalsCountry(c: Country, inputValue: string) {
  const _inputValue = inputValue.toLowerCase()
  const encodedEn = encodeDestination(c.en!)?.toLowerCase()
  const encodedBs = encodeDestination(c.bs!)?.toLowerCase()
  const encodedHr = encodeDestination(c.hr!)?.toLowerCase()
  const encodedSr = encodeDestination(c.sr!)?.toLowerCase()

  return encodedEn === _inputValue || encodedBs === _inputValue || encodedHr === _inputValue || encodedSr === _inputValue
}

export function getOfferTitle(offer: PlanDetails) {
  if (process.env.NODE_ENV === 'development') {
    return offer.formatedPlan + ' - ' + offer.id
  }
  return offer.formatedPlan
}

interface FilterDataArgs {
  ids?: string[]
  sortBy: SortOptionItem
  capacity: CapacityOptionItem[]
  validity: CapacityOptionItem[]
  isTopup?: boolean
}

export function filterData(data: PaginatedList<PlanDetails>, { ids, capacity, validity, sortBy, isTopup }: FilterDataArgs): PlanDetails[] {
  const filtered = data.items.filter(plan => {
    if (isTopup && plan.provider !== 'VR') {
      return false
    }

    if (!!ids?.length) {
      return ids?.includes(plan.id)
    }

    // Check if filtering by capacity or validity is needed
    const considerFilter = capacity.length > 0 || validity.length > 0
    if (!considerFilter) {
      return true // No filtering, return all items
    }

    // Check for capacity match
    const capacityMatch = capacity.length === 0 || capacity.some(c => c.config.some(q => plan.volume >= q.from && plan.volume <= q.to))

    // Check for validity match
    const validityMatch = capacityMatch || validity.some(v => v.config.some(q => plan.validity >= q.from && plan.validity <= q.to))

    // Return true if at least one condition is satisfied
    return capacityMatch || validityMatch
  })

  const [property, order] = sortBy.value.split('=')

  if (property === 'price' || property === 'capacity') {
    // first sort by volume and then by price
    return filtered.sort((a, b) => {
      const volumeA = a.volume
      const volumeB = b.volume
      if (volumeA !== volumeB) {
        return order === 'asc' ? volumeA - volumeB : volumeB - volumeA
      }

      return order === 'asc' ? a.price - b.price : b.price - a.price
    })
  } else if (property === 'validity') {
    return filtered.sort((a, b) => {
      const validityA = a.validity
      const validityB = b.validity
      if (validityA !== validityB) {
        return order === 'asc' ? validityA - validityB : validityB - validityA
      }

      return order === 'asc' ? a.price - b.price : b.price - a.price
    })
  }

  return filtered
}
